// Build timestamp: Cache invalidation force
import React, { useState, useEffect, useCallback } from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth } from './contexts/AuthContext';
import "./index.css";
import OutputTable from "./components/OutputTable";
import api from './api/client';
import {
    Container,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
    Typography,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    TextField,
    AppBar
} from '@mui/material';
import type { DataGrid, GridColDef } from '@mui/x-data-grid';
import SvgIcon from '@mui/material/SvgIcon';
import { Download, Settings, LogOut, Trash2 } from "lucide-react";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

interface DropdownOptions {
  analyticsEngineOptions: string[];
  fullAssetOptions: string[];
  onDemandOptions: number[];
  realTimeOptions: number[];
  researchOptions: number[];
  videoComplexityOptions: string[];
  resolutionOptions: string[];
}

interface DependentOptions {
  realTimeOptions: number[];
  researchOptions: number[];
}

interface ServerConfig {
  serverRole: string;
  sku: string;
  qty: number;
  toggleGroupID?: string;
  formFactor?: string;
}

// Debounce function
const debounce = <F extends (...args: any[]) => any>(func: F, delay: number) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: Parameters<F>) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func(...args), delay);
    };
};

// Add ExcelIcon component
function ExcelIcon(props: any) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <rect x="2" y="3" width="20" height="18" rx="2" fill="#217346" />
      <rect x="4" y="5" width="16" height="14" rx="1" fill="#fff" />
      <path d="M8.5 9.5L11 12l-2.5 2.5M15.5 9.5L13 12l2.5 2.5" stroke="#217346" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <rect x="4" y="5" width="4" height="14" rx="1" fill="#217346" />
      <text x="6" y="17" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#fff" fontFamily="Arial">X</text>
    </SvgIcon>
  );
}

// Custom tooltip styled to match label color
const LabelTooltip = styled(Tooltip)(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: '#374151', // Tailwind text-gray-700
    color: '#fff',
    fontSize: 13,
    fontWeight: 400,
    boxShadow: theme.shadows[1],
    borderRadius: 6,
    padding: '8px 12px',
  },
  [`& .MuiTooltip-arrow`]: {
    color: '#374151',
  },
}));

function App() {
  const location = useLocation();
  const { currentUser, isAdmin, logout } = useAuth();
  const [formData, setFormData] = useState({
    analyticsEngine: "OX5",
    fullAsset: "No",
    onDemand: 100,
    realTime: 0,
    research: 0,
    videoComplexity: "Low",
    resolution: "1080p"
  });

  const [customerData, setCustomerData] = useState({
    customerName: "",
    opportunityName: "",
    salesContact: "",
    date: new Date().toISOString().split('T')[0]
  });

  const [dropdownOptions, setDropdownOptions] = useState<DropdownOptions>({
    analyticsEngineOptions: [],
    fullAssetOptions: [],
    onDemandOptions: [],
    realTimeOptions: [],
    researchOptions: [],
    videoComplexityOptions: [],
    resolutionOptions: []
  });

  const [dependentOptions, setDependentOptions] = useState<DependentOptions>({
    realTimeOptions: [],
    researchOptions: []
  });

  const [configurations, setConfigurations] = useState<ServerConfig[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFormComplete, setIsFormComplete] = useState(false);
  const [toggleSelections, setToggleSelections] = useState<{ [groupID: string]: string }>({});

  const CUSTOMER_NAME_MAX = 60;
  const OPPORTUNITY_NAME_MAX = 80;
  const SALES_CONTACT_MAX = 50;

  // Fetch dropdown options when component mounts
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/options');
        const data: DropdownOptions = response.data;

        console.log("--- 📊 RECEIVED DROPDOWN OPTIONS (App.tsx) ---");
        console.log(JSON.stringify(data, null, 2));
        console.log("---------------------------------------------");

        setDropdownOptions(data);
      } catch (err) {
        setError('Failed to load options. Please try again.');
        console.error('Error fetching options:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOptions();
  }, []);

  // Listen for Excel upload success events
  useEffect(() => {
    const handleExcelUploadSuccess = async () => {
      console.log('🔄 Refreshing dropdown options after Excel upload...');
      try {
        setLoading(true);
        const response = await api.get('/api/options');
        const data: DropdownOptions = response.data;

        console.log("--- 📊 REFRESHED DROPDOWN OPTIONS (App.tsx) ---");
        console.log(JSON.stringify(data, null, 2));
        console.log("---------------------------------------------");

        setDropdownOptions(data);
        
        // Reset form data to default values to ensure compatibility with new options
        setFormData({
          analyticsEngine: data.analyticsEngineOptions[0] || "OX5",
          fullAsset: data.fullAssetOptions[0] || "No",
          onDemand: data.onDemandOptions[0] || 100,
          realTime: 0,
          research: 0,
          videoComplexity: data.videoComplexityOptions[0] || "Low",
          resolution: data.resolutionOptions[0] || "1080p"
        });
      } catch (err) {
        setError('Failed to refresh options after upload. Please refresh the page.');
        console.error('Error refreshing options:', err);
      } finally {
        setLoading(false);
      }
    };

    window.addEventListener('excelUploadSuccess', handleExcelUploadSuccess);
    
    return () => {
      window.removeEventListener('excelUploadSuccess', handleExcelUploadSuccess);
    };
  }, []);

  // Fetch dependent options when onDemand changes
  useEffect(() => {
    fetchDependentOptions(formData.onDemand);
  }, [formData.onDemand]);

  // Check if form is complete
  useEffect(() => {
    const isComplete = 
      formData.analyticsEngine !== "" &&
      formData.fullAsset !== "" &&
      formData.onDemand !== 0 &&
      formData.realTime !== undefined &&
      formData.research !== undefined &&
      formData.videoComplexity !== "" &&
      formData.resolution !== "";
    
    setIsFormComplete(isComplete);
  }, [formData]);

  const fetchDependentOptions = async (onDemand: number) => {
    try {
      const response = await api.get(`/api/dependent-options/${onDemand}`);
      const data = response.data;
      setDependentOptions(data);

      // Reset realTime and research if they're not in the new options
      setFormData(prev => {
        let newRealTime = prev.realTime;
        let newResearch = prev.research;
        if (!data.realTimeOptions.includes(prev.realTime)) {
          newRealTime = data.realTimeOptions[0] || 0;
        }
        if (!data.researchOptions.includes(prev.research)) {
          newResearch = data.researchOptions[0] || 0;
        }
        return {
          ...prev,
          realTime: newRealTime,
          research: newResearch
        };
      });
    } catch (err) {
      setError('Failed to load dependent options. Please try again.');
      console.error('Error fetching dependent options:', err);
    }
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setError(null);
    
    const allowedRealTime = [0, 20, 30, 40, 50];
    let newFormData = {
      ...formData,
      [name]: name === 'onDemand' || name === 'realTime' || name === 'research' 
        ? parseInt(value) 
        : name === 'videoComplexity' 
          ? value.replace(/^[\d]+\.\s*/, '')
          : value
    };

    // If Real-Time is set to 0, automatically reset Research to 0
    if (name === 'realTime' && parseInt(value) === 0) {
      newFormData.research = 0;
    }

    if (name === 'onDemand') {
      try {
        const response = await api.get(`/api/dependent-options/${value}`);
        const data = response.data;
        if (!allowedRealTime.includes(formData.realTime)) {
          newFormData.realTime = allowedRealTime[0];
        }
        newFormData.research = data.researchOptions[0] || 0;
        setDependentOptions(data);
      } catch (err) {
        setError('Failed to load dependent options. Please try again.');
        console.error('Error fetching dependent options:', err);
        return;
      }
    }

    if (name === 'onDemand' || name === 'realTime') {
      try {
        const response = await api.get(`/api/research-options/${newFormData.onDemand}/${newFormData.realTime}`);
        const data = response.data;
        setDependentOptions(prev => ({
          ...prev,
          researchOptions: data.researchOptions || []
        }));
        if (data.researchOptions && !data.researchOptions.includes(newFormData.research)) {
          newFormData.research = data.researchOptions[0] || 0;
        }
      } catch (err) {
        setError('Failed to load research options. Please try again.');
        console.error('Error fetching research options:', err);
        return;
      }
    }

    setFormData(newFormData);
  };

  // Add new useEffect for calculations
  useEffect(() => {
    const calculateConfigurations = async () => {
      if (!isFormComplete) return;
      
      setLoading(true);
      setError(null);
      try {
        console.log('Sending request with data:', formData);
        const response = await api.post('/api/calculate', formData);
        const data = response.data;
        if (!data.configurations) {
          throw new Error('Invalid response format');
        }
        setConfigurations(data.configurations);
      } catch (err) {
        setError('Failed to calculate configurations. Please try again.');
        console.error('Error calculating:', err);
      } finally {
        setLoading(false);
      }
    };

    calculateConfigurations();
  }, [formData, isFormComplete]);

  const handleCustomerInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let trimmed = value.trimStart();
    let max = 1000;
    if (name === 'customerName') max = CUSTOMER_NAME_MAX;
    if (name === 'opportunityName') max = OPPORTUNITY_NAME_MAX;
    if (name === 'salesContact') max = SALES_CONTACT_MAX;
    if (trimmed.length > max) trimmed = trimmed.slice(0, max);
    // Prevent year longer than 4 digits for date input
    if (name === 'date') {
      const year = trimmed.split('-')[0];
      if (year.length > 4) {
        return; // Ignore update if year is too long
      }
    }
    setCustomerData(prev => ({
      ...prev,
      [name]: trimmed
    }));
  };

  const handleClearAll = () => {
    setFormData({
      analyticsEngine: "OX5",
      fullAsset: "No",
      onDemand: 100,
      realTime: 0,
      research: 0,
      videoComplexity: "Low",
      resolution: "1080p"
    });
    setCustomerData({
      customerName: "",
      opportunityName: "",
      salesContact: "",
      date: new Date().toISOString().split('T')[0]
    });
    setConfigurations([]);
    setError(null);
  };

  // Add a helper to check if customer info is complete
  const isCustomerInfoComplete = customerData.customerName && customerData.opportunityName && customerData.salesContact;

  // Add a helper to check if the system is valid (no 'Missing Match' SKUs and at least one config)
  const isSystemValid = configurations.length > 0 && !configurations.some(cfg => cfg.sku === 'Missing Match');

  const handleExportToExcel = async () => {
    setLoading(true);
    try {
      // Filter configurations for export based on toggleSelections
      const grouped: { [key: string]: any[] } = {};
      configurations.forEach((config, idx) => {
        const groupKey = config.toggleGroupID && config.toggleGroupID !== '' ? config.toggleGroupID : `__${idx}`;
        if (!grouped[groupKey]) grouped[groupKey] = [];
        grouped[groupKey].push(config);
      });
      const filteredConfigs: any[] = [];
      Object.entries(grouped).forEach(([groupKey, group]) => {
        if (group.length > 1 && group[0].toggleGroupID) {
          // Toggle-eligible group: only export the selected config
          const selectedFormFactor = toggleSelections[groupKey] || group[0].formFactor;
          const selectedConfig = group.find(cfg => cfg.formFactor === selectedFormFactor) || group[0];
          filteredConfigs.push(selectedConfig);
        } else {
          // Not toggle-eligible: export as-is
          filteredConfigs.push(group[0]);
        }
      });
      const response = await api.post('/api/export', {
        customer_info: {
          name: customerData.customerName || "Unknown",
          organization: customerData.opportunityName || "Unknown",
          email: customerData.salesContact || "Unknown",
          date: customerData.date || ""
        },
        form_data: {
          analytics_engine: formData.analyticsEngine,
          on_demand: formData.onDemand,
          real_time: formData.realTime,
          research: formData.research,
          video_complexity: formData.videoComplexity,
          resolution: formData.resolution,
          form_factor: "Rack",
          next_gen_engine: "Yes",
          gpu_options: "3,2,1",
          fullAsset: formData.fullAsset
        },
        configurations: filteredConfigs.map(config => ({
          engine: formData.analyticsEngine,
          role: config.serverRole,
          sku: config.sku,
          qty: config.qty,
          fs_storage: "FS32",
          combined_sku: `${config.sku}-FS32`,
          husky_sku: config.sku.replace('BCD', 'HU'),
          match_flag: "✅"
        }))
      }, {
        responseType: 'blob'
      });

      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `BriefCam_BOM_${customerData.customerName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'report'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export to Excel. Please try again.');
      console.error('Error exporting to Excel:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      // Navigation will be handled by ProtectedRoute
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-8">
            <img 
              src="/milestone-logo.svg" 
              alt="Milestone" 
              className="h-10 w-auto"
            />
            <div>
              <h1 className="text-2xl font-medium text-[#0099da]">Briefcam Husky Calculator</h1>
            </div>
          </div>
          <div className="flex items-center space-x-4 ml-2">
            {isAdmin && (
              <button
                onClick={() => window.location.href = '/admin'}
                type="button"
                className="flex items-center bg-white text-[#0076A8] border border-[#0076A8] rounded-md px-4 py-2 text-sm font-medium transition-colors"
                style={{ transition: 'color 0.2s, border-color 0.2s, background 0.2s' }}
                onMouseEnter={e => {
                  e.currentTarget.style.color = '#005275';
                  e.currentTarget.style.borderColor = '#005275';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.color = '';
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.backgroundColor = '';
                }}
                onMouseDown={e => {
                  e.currentTarget.style.color = '#002F42';
                  e.currentTarget.style.borderColor = '#002F42';
                  e.currentTarget.style.backgroundColor = 'white';
                }}
                onMouseUp={e => {
                  e.currentTarget.style.color = '';
                  e.currentTarget.style.borderColor = '';
                  e.currentTarget.style.backgroundColor = '';
                }}
              >
                <Settings className="h-4 w-4 mr-2" />
                Admin
              </button>
            )}
            <button
              onClick={handleExportToExcel}
              disabled={!isCustomerInfoComplete || !isSystemValid}
              type="button"
              className="flex items-center bg-white text-[#0076A8] border border-[#0076A8] rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ transition: 'color 0.2s, border-color 0.2s, background 0.2s' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = '#005275';
                e.currentTarget.style.borderColor = '#005275';
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.backgroundColor = '';
              }}
              onMouseDown={e => {
                e.currentTarget.style.color = '#002F42';
                e.currentTarget.style.borderColor = '#002F42';
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onMouseUp={e => {
                e.currentTarget.style.color = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.backgroundColor = '';
              }}
            >
              <Download className="h-4 w-4 mr-2" />
              Export to Excel
            </button>
            <button
              onClick={handleClearAll}
              type="button"
              className="flex items-center bg-white text-[#C00707] border border-[#C00707] rounded-md px-4 py-2 text-sm font-medium transition-colors"
              style={{ transition: 'color 0.2s, border-color 0.2s, background 0.2s' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = '#8F0505';
                e.currentTarget.style.borderColor = '#8F0505';
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.backgroundColor = '';
              }}
              onMouseDown={e => {
                e.currentTarget.style.color = '#5D0303';
                e.currentTarget.style.borderColor = '#5D0303';
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onMouseUp={e => {
                e.currentTarget.style.color = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.backgroundColor = '';
              }}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear All
            </button>
            <button
              onClick={handleLogout}
              type="button"
              className="flex items-center bg-[#C00707] text-white border border-[#C00707] rounded-md px-4 py-2 text-sm font-medium hover:bg-[#8F0505] transition-colors"
              style={{ transition: 'background 0.2s, border-color 0.2s' }}
              onMouseDown={e => {
                e.currentTarget.style.backgroundColor = '#5D0303';
                e.currentTarget.style.borderColor = '#5D0303';
                e.currentTarget.style.color = 'white';
              }}
              onMouseUp={e => {
                e.currentTarget.style.backgroundColor = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.color = '';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '';
                e.currentTarget.style.borderColor = '';
                e.currentTarget.style.color = '';
              }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            {/* Analytics Engine Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-medium mb-3 text-milestone">Analytics Engine</h2>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">
                  Engine
                </label>
                <select
                  name="analyticsEngine"
                  value={formData.analyticsEngine}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                >
                  {dropdownOptions.analyticsEngineOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Channel Count Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-medium mb-3 text-milestone">Channel Count</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center gap-1">
                    On Demand
                    <LabelTooltip title="Number of Review channels processed on demand for investigations." arrow>
                      <InfoOutlinedIcon fontSize="inherit" color="action" style={{ cursor: 'pointer', fontSize: 16 }} />
                    </LabelTooltip>
                  </label>
                  <select
                    name="onDemand"
                    value={formData.onDemand}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                  >
                    {dropdownOptions.onDemandOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center gap-1">
                    Real Time
                    <LabelTooltip title="Number of Respond channels processed in real time for alerts or instant review." arrow>
                      <InfoOutlinedIcon fontSize="inherit" color="action" style={{ cursor: 'pointer', fontSize: 16 }} />
                    </LabelTooltip>
                  </label>
                  <select
                    name="realTime"
                    value={formData.realTime}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                  >
                    {dependentOptions.realTimeOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center gap-1">
                    Processed Daily
                    <LabelTooltip title="Number of channels processed daily for Research dashboard updates." arrow>
                      <InfoOutlinedIcon fontSize="inherit" color="action" style={{ cursor: 'pointer', fontSize: 16 }} />
                    </LabelTooltip>
                  </label>
                  <select
                    name="research"
                    value={formData.research}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                    disabled={formData.realTime === 0}
                  >
                    {dependentOptions.researchOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {formData.realTime === 0 && (
                    <p className="text-xs text-gray-500 mt-1">To choose Research Channels, first select Real Time Channels.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Video Settings Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-medium mb-3 text-milestone">Video Settings</h2>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Scene Complexity
                  </label>
                  <select
                    name="videoComplexity"
                    value={formData.videoComplexity}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                  >
                    {dropdownOptions.videoComplexityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-1">
                    Resolution
                  </label>
                  <select
                    name="resolution"
                    value={formData.resolution}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                  >
                    {dropdownOptions.resolutionOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-medium mb-3 text-milestone">Visual Assets Generation</h2>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1 flex items-center gap-1">
                  Processing level
                  <LabelTooltip
                    title={
                      <span>
                        Select 'Full Assets' if you want to utilize the processed video assets later for investigations (e.g., Instant Review). This option requires additional disk space.<br/>
                        Select 'Partial Assets' if you're primarily using Real-Time channels for alerts and dashboards (e.g., Respond, Research).<br/>
                        <em>Note: Partial Asset videos can still be reprocessed for investigation if needed.</em>
                      </span>
                    }
                    arrow
                  >
                    <InfoOutlinedIcon fontSize="inherit" color="action" style={{ cursor: 'pointer', fontSize: 16 }} />
                  </LabelTooltip>
                </label>
                <select
                  name="fullAsset"
                  value={formData.fullAsset}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-1.5"
                >
                  {dropdownOptions.fullAssetOptions.map(option => (
                    <option key={option} value={option}>{option === 'Yes' ? 'Full assets' : 'Partial assets'}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-4">
            {/* Customer Information Section */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-medium mb-3 text-milestone">Customer Information</h2>
              <div className="space-y-3">
                <div>
                  <input
                    type="text"
                    name="customerName"
                    value={customerData.customerName}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter customer name"
                    className="w-full rounded-md px-3 py-1.5 placeholder-gray-500 focus:outline-none"
                    style={{
                      border: '1.5px solid #D3DBDE',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#0099DA'}
                    onBlur={e => e.currentTarget.style.borderColor = '#D3DBDE'}
                    onMouseOver={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#758E99'; }}
                    onMouseOut={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#D3DBDE'; }}
                    maxLength={CUSTOMER_NAME_MAX}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="opportunityName"
                    value={customerData.opportunityName}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter opportunity name"
                    className="w-full rounded-md px-3 py-1.5 placeholder-gray-500 focus:outline-none"
                    style={{
                      border: '1.5px solid #D3DBDE',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#0099DA'}
                    onBlur={e => e.currentTarget.style.borderColor = '#D3DBDE'}
                    onMouseOver={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#758E99'; }}
                    onMouseOut={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#D3DBDE'; }}
                    maxLength={OPPORTUNITY_NAME_MAX}
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="salesContact"
                    value={customerData.salesContact}
                    onChange={handleCustomerInputChange}
                    placeholder="Enter sales contact"
                    className="w-full rounded-md px-3 py-1.5 placeholder-gray-500 focus:outline-none"
                    style={{
                      border: '1.5px solid #D3DBDE',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#0099DA'}
                    onBlur={e => e.currentTarget.style.borderColor = '#D3DBDE'}
                    onMouseOver={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#758E99'; }}
                    onMouseOut={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#D3DBDE'; }}
                    maxLength={SALES_CONTACT_MAX}
                  />
                </div>
                <div>
                  <input
                    type="date"
                    name="date"
                    value={customerData.date}
                    onChange={handleCustomerInputChange}
                    className="w-full rounded-md px-3 py-1.5 text-gray-500 focus:outline-none"
                    style={{
                      border: '1.5px solid #D3DBDE',
                      transition: 'border-color 0.2s',
                    }}
                    onFocus={e => e.currentTarget.style.borderColor = '#0099DA'}
                    onBlur={e => e.currentTarget.style.borderColor = '#D3DBDE'}
                    onMouseOver={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#758E99'; }}
                    onMouseOut={e => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderColor = '#D3DBDE'; }}
                  />
                </div>
              </div>
            </div>

            {/* Results Table */}
            <OutputTable
              configurations={configurations}
              loading={loading}
              error={error}
              toggleSelections={toggleSelections}
              setToggleSelections={setToggleSelections}
            />
            {/* Informational Banner */}
            <div className="bg-white border border-gray-200 p-6 text-left text-sm text-gray-800 rounded-md mt-4 space-y-2 flex items-start gap-3">
              <InfoOutlinedIcon className="mt-1" style={{ color: '#9ca3af', fontSize: 22, flexShrink: 0 }} />
              <div>
                <p><span className="font-semibold text-gray-800">This calculator supports the most common analytics configurations.</span><br />
                For larger-scale deployments, use the <a href="https://milestonesys365.sharepoint.com/:x:/r/sites/Presales-site-BriefCam/Shared%20Documents/Hardware%20Sizing%20Collateral/BriefCam%20v7%20Hardware%20Deployment%20Calculator%20V4.0a%20beta.xlsm?d=wd7c1b4ac317b41659d36fa829bc3b208&csf=1&web=1&e=pJ7fL2" target="_blank" rel="noopener noreferrer" className="underline text-blue-700 hover:text-blue-900">BriefCam Hardware Deployment Calculator</a>.</p>
                <p>Use the average scene complexity and resolution across all cameras.</p>
                <p>Retention periods are based on BriefCam defaults:</p>
                <ul className="list-disc pl-6">
                  <li>Original video footage is stored for 7 days</li>
                  <li>Processed video assets are retained for 30 days</li>
                  <li>RESEARCH dashboards display data from the past 30 days</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
